import {Component, computed, inject, input, output, signal} from '@angular/core';
import {ButtonComponent} from '../../../../shared/components/button/button.component';
import {IconsComponent} from '../../../../shared/components/icons/icons.component';
import {AppStore} from '../../../../shared/store/app.store';
import {NavigationService} from '../../../../shared/services/navigation.service';
import {ResultsStore} from '../../../../shared/store/results.store';
import {DialogService} from '../../../../shared/services/dialog.service';
import {CompareComponent} from '../../compare.component';
import {AccessKey, AccessService} from '../../../../shared/services/access.service';
import {getSourcesByType} from '../../../../shared/utils/availability.utils';
import {IItem} from '../../../../types/IItem';
import {StorageService} from '../../../../shared/services/storage.service';

type StepKey = 'quote' | 'quote-po' | 'quote-po-submit';

@Component({
  selector: 'app-compare-footer',
  standalone: true,
  imports: [
    ButtonComponent,
    IconsComponent,
  ],
  templateUrl: './compare-footer.component.html',
  styleUrl: './compare-footer.component.css',
})
export class CompareFooterComponent {
  buttonsDisabled = input<boolean>(false);

  onConfirm = output<'quote' | 'quote-po' | 'quote-po-submit'>();
  saveForLater = output<void>();

  appStore = inject(AppStore);
  access = inject(AccessService);
  private resultsStore = inject(ResultsStore);
  private navigation = inject(NavigationService);
  private dialogService = inject(DialogService);
  private compareComponent = inject(CompareComponent)
  private storage = inject(StorageService);

  readonly steps = [
    {key: 'quote', label: 'Quote',   icon: 'add-column-before', title: 'Saves to the quote. No PO created.'},
    {key: 'quote-po',   label: 'Open PO', icon: 'file-pen', title: 'Adds to quote and stages on the open PO (editable).'},
    {key: 'quote-po-submit', label: 'Order',   icon: 'truck', title: 'Adds to quote, locks PO, and submits to distributor.'},
  ] as {key: StepKey, icon: string, label: string, title: string}[];

  visibleSteps = computed(() => this.steps.filter(s => !this.isStepHidden(s.key)));

  private manualStep = signal<StepKey>(
    (this.storage.get('last-order-step') as StepKey) ?? 'quote'
  );
  private hoveredStep = signal<StepKey | null>(null);

  selectedStep = computed<StepKey>(() => {
    const candidates = this.visibleSteps();
    const manual = this.manualStep();

    if (candidates.some(s => s.key === manual) && !this.isStepDisabled(manual)) {
      return manual;
    }

    for (let i = candidates.length - 1; i >= 0; i--) {
      if (!this.isStepDisabled(candidates[i].key)) return candidates[i].key;
    }

    return manual;
  });

  displayStep = computed<StepKey>(() => this.hoveredStep() ?? this.selectedStep());
  displayStepIndex = computed(() => this.visibleSteps().findIndex(s => s.key === this.displayStep()));

  // selectedStep() falls back to the last manually-picked step when every visible
  // step is disabled, so Confirm needs its own guard against submitting that step.
  readonly allStepsDisabled = computed(() => this.visibleSteps().every(step => this.isStepDisabled(step.key)));
  readonly confirmDisabled = computed(() => this.buttonsDisabled() || this.allStepsDisabled());
  readonly confirmTitle = computed(() => {
    if (!this.allStepsDisabled()) return 'Confirm and submit action';

    const step = this.steps.find(s => s.key === this.displayStep());
    return step ? this.getStepTitle(step) : 'Confirm and submit action';
  });

  onStepHover(key: StepKey): void {
    this.hoveredStep.set(key);
  }

  onStepLeave(): void {
    this.hoveredStep.set(null);
  }

  select(key: StepKey): void {
    this.manualStep.set(key);
    this.hoveredStep.set(null);
    this.storage.set('last-order-step', key);
  }

  private readonly STEP_ACCESS: Record<StepKey, {base: AccessKey[]; transfer?: AccessKey; distributor?: AccessKey}> = {
    quote: {base: ['add_to_quote', 'create_quote']},
    'quote-po': {base: ['add_to_quote', 'create_quote'], transfer: 'create_transfer', distributor: 'create_purchase_order'},
    'quote-po-submit': {base: ['add_to_quote', 'create_quote'], transfer: 'submit_transfer', distributor: 'submit_purchase_order'},
  };

  private permissionIssue(step_key: StepKey, item: IItem): AccessKey | 'no_sourcing' | null {
    const config = this.STEP_ACCESS[step_key];

    for (const key of config.base) {
      if (this.access.cannot(key)) return key;
    }

    const hasTransferSources = !!getSourcesByType(item.sourcing, 'transfer').length;
    const hasDistributorSources = !!getSourcesByType(item.sourcing, 'distributor').length;

    if (hasTransferSources && config.transfer && this.access.cannot(config.transfer)) {
      return config.transfer;
    }

    if (hasDistributorSources && config.distributor && this.access.cannot(config.distributor)) {
      return config.distributor;
    }

    if (config.transfer && !hasTransferSources && !hasDistributorSources) {
      return 'no_sourcing';
    }

    return null;
  }

  isStepDisabled(step_key: StepKey): boolean {
    const item = this.compareComponent.selectedItem();

    if (!item) return true;

    return this.permissionIssue(step_key, item) !== null;
  }

  isStepHidden(step_key: StepKey): boolean {
    const config = this.STEP_ACCESS[step_key];

    if (config.base.some(key => this.access.lacks(key))) {
      return true;
    }

    if (!config.transfer && !config.distributor) {
      return false;
    }

    return (!config.transfer || this.access.lacks(config.transfer))
      || (!config.distributor || this.access.lacks(config.distributor));
  }

  getStepTitle(step: {key: StepKey, title: string}): string {
    const item = this.compareComponent.selectedItem();

    if (!item) return step.title;

    const issue = this.permissionIssue(step.key, item);

    if (!issue) return step.title;

    return issue === 'no_sourcing' ? 'No transfer or distributor in sourcing' : this.access.getTitle(issue);
  }

  startOver() {
    this.dialogService.confirm("This will clear your cart. If you've shared a quote with your customer, that link will stop working. Continue?").then((result) => {
      if (result) {
        this.appStore.clearSnapshot();
        this.resultsStore.clearResults();
        this.resultsStore.clearCompare();
        this.resultsStore.clearPricing();
        this.navigation.navigate('lookup', {replaceUrl: true});
      }
    });
  }
}
